import React, { useState, useEffect } from 'react';
import { NavigationProvider } from '../../components/navigation/NavigationProvider';
import { PrimaryNavigation } from '../../components/navigation/PrimaryNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import GoalCard from './components/GoalCard';
import GoalModal from './components/GoalModal';
import GoalTimeline from './components/GoalTimeline';
import GoalComparison from './components/GoalComparison';
import GoalStats from './components/GoalStats';
import EmptyState from './components/EmptyState';
import { generateLifeGoalsPDF } from '../../utils/pdfExport';

const LifeGoalsManagement = () => {
  const [goals, setGoals] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Function to load goals with retirement data
  const loadGoalsWithRetirement = () => {
    const savedGoals = localStorage.getItem('lifeGoals');
    const retirementData = JSON.parse(localStorage.getItem('retirementProjection') || 'null');
    
    let goalsArray = [];
    
    // Parse saved goals if they exist
    if (savedGoals) {
      try {
        goalsArray = JSON.parse(savedGoals);
      } catch (error) {
        console.error('Error parsing saved goals:', error);
        goalsArray = [];
      }
    }
    
    // ALWAYS add/update retirement goal if retirement data exists
    if (retirementData && retirementData?.totalCorpusRequired) {
      const retirementGoal = {
        id: 'retirement-goal',
        name: 'Retirement Planning',
        type: 'retirement',
        targetAmount: retirementData?.totalCorpusRequired || 0,
        currentSavings: 0,
        targetDate: calculateRetirementDate(),
        expectedReturns: 10,
        inflationRate: 6,
        monthlySavingsRequired: retirementData?.monthlySavingsRequired || 0,
        createdAt: new Date()?.toISOString()
      };
      
      // Remove existing retirement goal if present
      goalsArray = goalsArray?.filter(goal => goal?.id !== 'retirement-goal');
      
      // Add retirement goal at the beginning
      goalsArray = [retirementGoal, ...goalsArray];
    }
    
    // Update state and localStorage
    setGoals(goalsArray);
    localStorage.setItem('lifeGoals', JSON.stringify(goalsArray));
  };

  useEffect(() => {
    // Initial load
    loadGoalsWithRetirement();
    
    // Listen for storage changes (from other tabs or windows)
    const handleStorageChange = (e) => {
      if (e?.key === 'retirementProjection' || e?.key === 'lifeGoals') {
        loadGoalsWithRetirement();
      }
    };
    
    // Listen for custom event (same tab updates)
    const handleRetirementUpdate = () => {
      loadGoalsWithRetirement();
    };
    
    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('retirementDataUpdated', handleRetirementUpdate);
    
    // Reload when component becomes visible (navigation back to this page)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadGoalsWithRetirement();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('retirementDataUpdated', handleRetirementUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const calculateRetirementDate = () => {
    try {
      const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      const retirementPlan = JSON.parse(localStorage.getItem('retirementPlan') || '{}');
      
      const calculateCurrentAge = (dobString) => {
        if (!dobString) return 32;
        const [day, month, year] = dobString?.split('/')?.map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today?.getFullYear() - birthDate?.getFullYear();
        const monthDiff = today?.getMonth() - birthDate?.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
          age--;
        }
        return age;
      };
      
      const currentAge = userProfile?.dateOfBirth ? calculateCurrentAge(userProfile?.dateOfBirth) : 32;
      const retirementAge = retirementPlan?.retirementAge || 60;
      const yearsToRetirement = retirementAge - currentAge;
      
      const retirementDate = new Date();
      retirementDate?.setFullYear(retirementDate?.getFullYear() + yearsToRetirement);
      
      return retirementDate?.toISOString()?.split('T')?.[0];
    } catch (error) {
      console.error('Error calculating retirement date:', error);
      // Default to 30 years from now
      const defaultDate = new Date();
      defaultDate?.setFullYear(defaultDate?.getFullYear() + 30);
      return defaultDate?.toISOString()?.split('T')?.[0];
    }
  };

  const saveGoalsToStorage = (updatedGoals) => {
    localStorage.setItem('lifeGoals', JSON.stringify(updatedGoals));
  };

  const handleSaveGoal = (goalData) => {
    if (editingGoal) {
      const updatedGoals = goals?.map(goal => 
        goal?.id === goalData?.id ? goalData : goal
      );
      setGoals(updatedGoals);
      saveGoalsToStorage(updatedGoals);
    } else {
      const newGoals = [...goals, goalData];
      setGoals(newGoals);
      saveGoalsToStorage(newGoals);
    }
    setEditingGoal(null);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setIsModalOpen(true);
  };

  const handleDeleteGoal = (goalId) => {
    // Prevent deletion of retirement goal
    if (goalId === 'retirement-goal') {
      alert('Retirement planning goal cannot be deleted. Please update it from the Retirement Planning page.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this goal?')) {
      const updatedGoals = goals?.filter(goal => goal?.id !== goalId);
      setGoals(updatedGoals);
      saveGoalsToStorage(updatedGoals);
    }
  };

  const handleViewDetails = (goal) => {
    setEditingGoal(goal);
    setIsModalOpen(true);
  };

  const handleCreateGoal = () => {
    setEditingGoal(null);
    setIsModalOpen(true);
  };

  const handleExportGoals = () => {
    const dataStr = JSON.stringify(goals, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `life-goals-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    if (goals?.length === 0) {
      alert('No goals to export. Please add goals first.');
      return;
    }

    // Get user data from localStorage
    const getUserData = () => {
      try {
        const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        
        const calculateCurrentAge = (dobString) => {
          if (!dobString) return 32;
          
          const [day, month, year] = dobString?.split('/')?.map(Number);
          const birthDate = new Date(year, month - 1, day);
          const today = new Date();
          let age = today?.getFullYear() - birthDate?.getFullYear();
          const monthDiff = today?.getMonth() - birthDate?.getMonth();
          
          if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
            age--;
          }
          
          return age;
        };

        return {
          name: userProfile?.fullName || 'User',
          currentAge: userProfile?.dateOfBirth ? calculateCurrentAge(userProfile?.dateOfBirth) : 32,
          fullName: userProfile?.fullName || userProfile?.name || '-',
          email: userProfile?.email || '-',
          dateOfBirth: userProfile?.dateOfBirth || '-',
        };
      } catch (error) {
        return { name: 'User', currentAge: 32, fullName: '-', email: '-', dateOfBirth: '-' };
      }
    };

    const userData = getUserData();

    // Ask user for export preference
    const useEnhanced = window.confirm(
      'Would you like to export the PDF with exact web page design?\n\n' +
      'Click "OK" for visual design matching (larger file size)\n' +
      'Click "Cancel" for standard formatted PDF (smaller file size)'
    );

    const result = await generateLifeGoalsPDF(userData, goals, useEnhanced);
    
    if (result?.success) {
      alert(`PDF exported successfully: ${result?.fileName}`);
    } else {
      alert(`Failed to export PDF: ${result?.error || 'Unknown error'}`);
    }
  };

  const filteredGoals = goals?.filter(goal => {
    const matchesSearch = goal?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterType === 'all' || goal?.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutGrid' },
    { id: 'timeline', label: 'Timeline', icon: 'Calendar' },
    { id: 'comparison', label: 'Comparison', icon: 'BarChart3' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Goals' },
    { value: 'retirement', label: 'Retirement' },
    { value: 'car', label: 'Car' },
    { value: 'home', label: 'Home' },
    { value: 'education', label: 'Education' },
    { value: 'wedding', label: 'Wedding' },
    { value: 'custom', label: 'Custom' }
  ];

  return (
    <NavigationProvider>
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        
        <main className="life-goals-page main-content main-content-desktop">
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Life Goals Management</h1>
                <p className="text-muted-foreground">Plan and track your financial objectives for a secure future</p>
              </div>
              <div className="flex items-center gap-3">
                {goals?.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      iconName="FileText"
                      iconPosition="left"
                      onClick={handleExportPDF}
                    >
                      Export PDF
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Download"
                      iconPosition="left"
                      onClick={handleExportGoals}
                    >
                      Export JSON
                    </Button>
                  </>
                )}
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleCreateGoal}
                >
                  Add New Goal
                </Button>
              </div>
            </div>

            {goals?.length > 0 && <GoalStats goals={goals} />}
          </div>

          {goals?.length === 0 ? (
            <EmptyState onCreateGoal={handleCreateGoal} />
          ) : (
            <>
              <div className="bg-card rounded-xl border border-border mb-6">
                <div className="border-b border-border">
                  <div className="flex items-center overflow-x-auto">
                    {tabs?.map(tab => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon name={tab?.icon} size={20} />
                        <span>{tab?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {activeTab === 'overview' && (
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                      <div className="flex-1 relative">
                        <Icon 
                          name="Search" 
                          size={20} 
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                        />
                        <input
                          type="text"
                          placeholder="Search goals..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e?.target?.value)}
                          className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                        />
                      </div>
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e?.target?.value)}
                        className="px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                      >
                        {filterOptions?.map(option => (
                          <option key={option?.value} value={option?.value}>
                            {option?.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {filteredGoals?.length === 0 ? (
                      <div className="text-center py-12">
                        <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                        <p className="text-muted-foreground">No goals found matching your criteria</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredGoals?.map(goal => (
                          <GoalCard
                            key={goal?.id}
                            goal={goal}
                            onEdit={handleEditGoal}
                            onDelete={handleDeleteGoal}
                            onViewDetails={handleViewDetails}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'timeline' && (
                  <div className="p-6">
                    <GoalTimeline goals={filteredGoals} />
                  </div>
                )}

                {activeTab === 'comparison' && (
                  <div className="p-6">
                    <GoalComparison goals={filteredGoals} />
                  </div>
                )}
              </div>
            </>
          )}
        </main>

        <GoalModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingGoal(null);
          }}
          onSave={handleSaveGoal}
          editingGoal={editingGoal}
        />
      </div>
    </NavigationProvider>
  );
};

export default LifeGoalsManagement;